import { POTION_ICONS } from './PotionIcons';
import { WEAPON_ICONS } from './WeaponIcons';
import { ARMOR_ICONS } from './ArmorIcons';
import { MATERIAL_ICONS } from './MaterialIcons';
import { CURRENCY_ICONS } from './CurrencyIcons';
import { MOB_DROP_ICONS } from './MobDropIcons';
import { QUEST_ITEM_ICONS } from './QuestItemIcons';
import { LEGENDARY_WEAPON_ICONS } from './procedural/LegendaryWeaponIcons';
import { inferIconLook, type IconKind } from './procedural/iconLook';
import { WEAPON_KIND_ICONS } from './procedural/weaponIcons';
import { ARMOR_KIND_ICONS } from './procedural/armorIcons';
import { MATERIAL_KIND_ICONS } from './procedural/materialIcons';
import { CONSUMABLE_KIND_ICONS } from './procedural/consumableIcons';
import { svgFromBase64 } from '@/game/rendering/svg/SVGUtils';
import { ITEM_DATABASE } from '@/shared/content/generated/items.gen';
import type { ItemDefinition } from '@/shared/content/generated/items.gen';

const ALL_ICONS: Record<string, (size?: number) => string> = {
  ...POTION_ICONS,
  ...WEAPON_ICONS,
  ...ARMOR_ICONS,
  ...MATERIAL_ICONS,
  ...CURRENCY_ICONS,
  ...MOB_DROP_ICONS,
  ...QUEST_ITEM_ICONS,
  ...LEGENDARY_WEAPON_ICONS,
};

/**
 * A procedural icon generator for one IconKind. Receives the full item (so
 * it can read rarity/tint/stats) + the requested pixel size, and returns a
 * base64 `data:image/svg+xml` URL (the `framedIcon` convention — see
 * `procedural/rarityFrame.ts`).
 */
export type ProceduralIconGenerator = (item: ItemDefinition, size: number) => string;

/**
 * Kind-keyed procedural icon map. Starts EMPTY in this foundation task —
 * Tasks 4-6 populate it (via `registerProceduralIcons`) from their own
 * per-kind-family generator files. Until then every non-bespoke item falls
 * straight through to `fallbackIcon`, same as before this ladder rung existed.
 */
const proceduralIcons = new Map<IconKind, ProceduralIconGenerator>();

/**
 * Registration hook for later tasks: call once per generator file with a
 * partial `{ [kind]: generator }` map. Safe to call multiple times (e.g.
 * once per Task 4/5/6 file) — later registrations for the same kind win.
 */
export function registerProceduralIcons(generators: Partial<Record<IconKind, ProceduralIconGenerator>>): void {
  for (const kind of Object.keys(generators) as IconKind[]) {
    const generator = generators[kind];
    if (generator) proceduralIcons.set(kind, generator);
  }
}

// Kind-family registrations (Tasks 4-6). Composed HERE — not in the
// generator files — so the registry stays the single composition seam and
// each family file only needs a type-only import back (no runtime cycle).
registerProceduralIcons(WEAPON_KIND_ICONS); // Task 4: 10 weapon kinds
registerProceduralIcons(ARMOR_KIND_ICONS); // Task 5: 12 armor kinds
registerProceduralIcons(MATERIAL_KIND_ICONS); // Task 6: 10 material kinds
registerProceduralIcons(CONSUMABLE_KIND_ICONS); // Task 6: 8 consumable kinds + coin

/** Resolution ladder rung 2: bespoke misses fall here before the letter fallback. */
function getProceduralItemIcon(itemId: string, size: number): string | undefined {
  const item = ITEM_DATABASE[itemId];
  if (!item) return undefined;
  const { kind } = inferIconLook(item);
  const generator = proceduralIcons.get(kind);
  return generator ? generator(item, size) : undefined;
}

/**
 * Returns raw SVG string for a given item ID.
 * Used for React inline rendering.
 *
 * Resolution ladder: bespoke `ALL_ICONS[itemId]` -> procedural (keyed by the
 * item's inferred `IconKind`, empty until Tasks 4-6 register generators) ->
 * `fallbackIcon`.
 */
export function getItemIconInlineSVG(itemId: string, size: number = 32): string {
  const generator = ALL_ICONS[itemId];
  if (generator) {
    return generator(size);
  }
  const procedural = getProceduralItemIcon(itemId, size);
  if (procedural) {
    return svgFromBase64(procedural);
  }
  return fallbackIcon(itemId, size);
}

/**
 * Returns base64 data URL for a given item ID.
 * Used for Phaser texture loading.
 */
export function getItemIconSVG(itemId: string, size: number = 32): string {
  const svg = getItemIconInlineSVG(itemId, size);
  return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
}

/**
 * Check if we have a custom, hand-authored SVG icon for this item.
 * NOTE: semantics are BESPOKE-ONLY — deliberately does not report true for
 * procedural-ladder hits. Callers use this to decide hand-authored coverage
 * (e.g. the Coil Art Pack contract tests), not "will this render something
 * better than the letter fallback."
 */
export function hasItemIcon(itemId: string): boolean {
  return itemId in ALL_ICONS;
}

function fallbackIcon(itemId: string, size: number): string {
  const initial = (itemId[0] ?? '?').toUpperCase();
  return `<svg width="${size}" height="${size}" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="4" width="24" height="24" rx="4" fill="#555" stroke="rgba(255,255,255,0.2)" stroke-width="1"/>
    <text x="16" y="20" text-anchor="middle" font-size="12" font-family="monospace" fill="#ccc">${initial}</text>
  </svg>`;
}
